// @ts-nocheck
import React, { useState, useMemo, useCallback } from 'react';
import ClipLoader from 'react-spinners/ClipLoader';
import { useProfileSearch } from '../hooks/useProfileSearch.ts';
import SearchIcon from '../icons/search.svg';
import ContactsIcon from '../icons/contacts.svg';
import EditIcon from '../icons/edit.svg';
import AddContactIcon from '../icons/add-contact.svg';
import MessageIcon from '../icons/message.svg';
import WalletIcon from '../icons/wallet.svg'; // Make sure you have this icon
import TimerIcon from '../icons/timer.svg'; // Make sure you have this icon
import profil1 from '../assets/profil1.jpg';
import profil2 from '../assets/profil2.png';
import profil3 from '../assets/profil3.png';

// Helper functions (can be moved to utils)
const getPictureSrc = (filename) => {
    switch (filename) {
        case 'profil1.jpg': return profil1;
        case 'profil2.png': return profil2;
        case 'profil3.png': return profil3;
        default: return profil1;
    }
};
const getBorderStyle = (filename) => {
    // Simplified border style logic
    return 'border-gray-500 shadow-md';
};

// Add new interfaces for wallet and streams
interface PrivateStream {
    _id: string;
    streamerAddress: string;
    invitedAddress: string;
    entryPriceBNB: number;
    scheduleInfo: string;
    status: 'pending' | 'paid' | 'active' | 'completed' | 'rejected';
    streamerUsername?: string;
    invitedUsername?: string;
    updatedAt: string;
    streamStartTime?: Date; // Campo calculado para la fecha de inicio
}

// Agregar prop para manejar el inicio del stream
interface ProfileHeaderProps {
    profileAddress: string | null;
    connectedAddress: string | null;
    profileUsername: string | null;
    profilePicture: string;
    isProfileLoading: boolean;
    isContactLoading: boolean; // Loading state for add/remove contact actions
    isAlreadyContact: boolean;
    isOwnProfile: boolean;
    onOpenContactModal: () => void;
    onOpenUsernameModal: () => void;
    onOpenPictureModal: () => void;
    onAddContact: () => void;
    onSendMessage: () => void; // Function to open chat
    onOpenStreamConfigModal: () => void; // <-- Add new prop for stream config modal
    // --- Add Prop for Unread Count ---
    totalUnreadCount: number;
    // --- End Add Prop ---
    
    // Add new props for wallet and streams
    walletBalance?: string;
    isLoadingWallet?: boolean;
    privateStreams?: PrivateStream[];
    isLoadingStreams?: boolean;
    onWithdraw?: () => void;
    walletInfo?: {
        address: string;
        balance: string;
        network: string;
        symbol: string;
    };
    onStartStream?: (streamId: string) => void; // Agregar función para iniciar stream
    activeStreams: Array<{
        _id: string;
        streamerAddress: string;
        invitedAddress: string;
        status: string;
        streamStartTime: Date | null;
        shouldStart: boolean;
        timeRemaining: string;
        isHost: boolean;
        otherParty: {
          role: string;
          address: string;
          username: string;
        };
      }>; // Add activeStreams prop
    processedStreams?: Array<{
        _id: string;
        streamerAddress: string;
        invitedAddress: string;
        status: string;
        streamStartTime: Date | null;
        shouldStart: boolean;
        timeRemaining: string;
        isHost: boolean;
        isGuest: boolean;
        otherParty: {
          role: string;
          address: string;
          username: string;
        };
        isPending: boolean;
        isPaid: boolean;
        isActive: boolean;
        summary: string;
        entryPriceBNB: number;
      }>; // Add processedStreams prop
    onPayForStream?: (stream: PrivateStream) => void; // Add new prop for handling payment
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
    profileAddress,
    connectedAddress,
    profileUsername,
    profilePicture,
    isProfileLoading,
    isContactLoading,
    isAlreadyContact,
    isOwnProfile,
    onOpenContactModal,
    onOpenUsernameModal,
    onOpenPictureModal,
    onAddContact,
    onSendMessage,
    onOpenStreamConfigModal, // <-- Destructure the new prop
    // --- Destructure Unread Count Prop ---
    totalUnreadCount,
    // --- End Destructure ---
    
    // Destructure new props
    walletBalance = '0.0000',
    isLoadingWallet = false,
    privateStreams = [],
    isLoadingStreams = false,
    onWithdraw = () => {},
    onStartStream = () => {}, // Agregar función por defecto
    walletInfo,
    activeStreams, // Destructure activeStreams prop
    processedStreams = [], // Destructure processedStreams prop
    onPayForStream, // Add new prop for handling payment
}) => {
    const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
    const [withdrawAmount, setWithdrawAmount] = useState('');
    
    const {
        searchTerm,
        suggestions,
        showSuggestions,
        handleSearchChange,
        handleSuggestionClick,
        handleSearchSubmit,
        handleSearchKeyPress,
        hideSuggestions,
        showSuggestionsOnFocus,
    } = useProfileSearch(); // Use the custom hook
    
    // Add debugging log for wallet props
    console.log('[ProfileHeader DEBUG] Received wallet props:', { 
        walletBalance, 
        isLoadingWallet,
        walletInfo: walletInfo ? {
            address: walletInfo.address,
            balance: walletInfo.balance,
            symbol: walletInfo.symbol,
            network: walletInfo.network
        } : 'null'
    });
    
    // Agregar log adicional para ver contenido completo
    if (walletInfo) {
        console.log('[ProfileHeader DEBUG] walletInfo completo:', JSON.stringify(walletInfo));
    }

    // Improve the stream rendering function to reduce re-renders and fix role detection
    const renderActiveStreams = useCallback(() => {
        if (isLoadingStreams) {
          return (
            <div className="flex items-center justify-center py-1">
              <ClipLoader color={'#ffffff'} size={16} />
              <span className="ml-2 text-sm text-gray-400">Loading streams...</span>
            </div>
          );
        }
        
        // If no streams active for display
        if (!activeStreams || activeStreams.length === 0) {
          return (
            <div className="text-sm text-gray-400 py-1 text-center">
              No active streams found
            </div>
          );
        }
        
        return (
          <div className="space-y-2 text-sm">
            {activeStreams.map(stream => (
              <div key={stream._id} className="bg-gray-700 bg-opacity-40 p-2 rounded">
                <div className="flex justify-between items-center">
                  <span className="font-medium">
                    {stream.isHost 
                      ? `Hosting for: ${stream.otherParty?.username || 'Unknown User'}` 
                      : `Invited by: ${stream.otherParty?.username || 'Unknown User'}`}
                  </span>
                  <span className={`${stream.status === 'active' ? 'text-green-400' : 'text-yellow-400'} text-xs`}>
                    {stream.status?.toUpperCase() || 'PENDING'}
                  </span>
                </div>
                
                <div className="mt-1 flex items-center justify-between">
                  <div className="text-gray-400 text-xs">
                    {stream.status === 'paid' ? (
                      <span>Starts in: {stream.timeRemaining}</span>
                    ) : (
                      <span>Stream in progress</span>
                    )}
                  </div>
                  
                  {stream.shouldStart && stream.isHost && (
                    <button
                      onClick={() => onStartStream(stream._id)}
                      className="bg-green-600 hover:bg-green-700 text-white text-xs py-1 px-2 rounded"
                    >
                      Start Stream
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      }, [activeStreams, isLoadingStreams, onStartStream]);

    // Improved stream rendering with support for pending invites
    const renderStreams = useCallback(() => {
        if (isLoadingStreams) {
          return (
            <div className="flex items-center justify-center py-1">
              <ClipLoader color={'#ffffff'} size={16} />
              <span className="ml-2 text-sm text-gray-400">Loading streams...</span>
            </div>
          );
        }
        
        // Debug: Log what streams are being received
        console.log('[ProfileHeader] processedStreams:', processedStreams?.map(s => ({
          id: s._id,
          status: s.status,
          isPending: s.isPending,
          isGuest: s.isGuest,
          summary: s.summary,
          // Add debugging for otherParty
          otherParty: s.otherParty ? {
            exists: true,
            username: s.otherParty.username || 'missing username'
          } : 'missing otherParty'
        })));
        
        if (!processedStreams || processedStreams.length === 0) {
          return (
            <div className="text-sm text-gray-400 py-1 text-center">
              No streams found
            </div>
          );
        }
        
        return (
          <div className="space-y-2 text-sm">
            {processedStreams.map(stream => {
              // Ensure otherParty and username exist with defaults
              const otherPartyUsername = stream.otherParty?.username || 'Unknown User';
              
              return (
                <div key={stream._id} className="bg-gray-700 bg-opacity-40 p-2 rounded">
                  {/* Different UI based on stream state */}
                  {stream.isPending && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-yellow-300">
                          {stream.isGuest ? 'Invitación Recibida' : 'Invitación Enviada'}
                        </span>
                        <span className="bg-yellow-600 px-2 py-0.5 rounded text-xs">
                          PENDIENTE
                        </span>
                      </div>
                      
                      <div className="mt-1">
                        <p>{stream.summary || `Stream with ${otherPartyUsername}`}</p>
                        {/* ...existing code... */}
                      </div>
                      
                      {/* ...existing code... */}
                    </>
                  )}
                  
                  {/* Display for paid streams */}
                  {stream.isPaid && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="font-medium">
                          {stream.isHost 
                            ? `Anfitrión de: ${otherPartyUsername}` 
                            : `Invitado por: ${otherPartyUsername}`}
                        </span>
                        <span className="bg-blue-600 px-2 py-0.5 rounded text-xs">
                          PAGADO
                        </span>
                      </div>
                      
                      {/* ...existing code... */}
                    </>
                  )}
                  
                  {/* Display for active streams */}
                  {stream.isActive && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="font-medium">
                          {stream.isHost 
                            ? `Transmitiendo a: ${otherPartyUsername}` 
                            : `Viendo stream de: ${otherPartyUsername}`}
                        </span>
                        <span className="bg-green-600 px-2 py-0.5 rounded text-xs">
                          EN VIVO
                        </span>
                      </div>
                      
                      {/* ...existing code... */}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        );
      }, [processedStreams, isLoadingStreams, onPayForStream]);

    // Memoize wallet display to prevent re-renders
    const renderWalletInfo = useMemo(() => {
        const displayBalance = walletInfo?.balance 
          ? parseFloat(walletInfo.balance).toFixed(4) 
          : parseFloat(walletBalance).toFixed(4);
        
        // Only log during changes, not on every render
        const displayValue = displayBalance || '0.0000';
        
        return (
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-green-400 font-bold">{displayValue}</span>
              <span className="ml-1 text-gray-400">{walletInfo?.symbol || 'BNB'}</span>
            </div>
            <button 
              onClick={onWithdraw}
              disabled={parseFloat(displayValue) <= 0}
              className="text-xs bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:text-gray-400 text-white py-1 px-2 rounded transition-colors"
            >
              Withdraw
            </button>
          </div>
        );
      }, [walletInfo?.balance, walletBalance, walletInfo?.symbol, onWithdraw]);

    return (
        <>
            {/* --- Top Section: Search, Profile Info, Actions --- */}
            <div className="flex flex-col sm:flex-row justify-between items-start mb-8 gap-4">
                {/* Search Bar & Suggestions */}
                <div className="relative w-full sm:w-auto">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search user or address..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                            onFocus={showSuggestionsOnFocus} // Use hook handler
                            onBlur={hideSuggestions} // Use hook handler
                            className="bg-gray-700 text-white border border-gray-600 rounded-full py-2 px-4 pl-10 w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-[#7db4e5]"
                        />
                        <img src={SearchIcon} alt="Search" className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        <button onClick={handleSearchSubmit} className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-white">
                            Go
                        </button>
                    </div>
                    {/* Suggestions Dropdown - Correctly placed inside relative container */}
                    {showSuggestions && suggestions.length > 0 && (
                        <ul className="absolute z-10 w-full sm:w-64 mt-1 bg-gray-800 border border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {suggestions.map((user) => (
                                <li
                                    key={user.walletAddress}
                                    onMouseDown={() => handleSuggestionClick(user.walletAddress)} // Use onMouseDown to fire before onBlur
                                    className="px-4 py-2 text-sm text-white hover:bg-gray-700 cursor-pointer truncate"
                                >
                                    {user.username} <span className="text-xs text-gray-400 block truncate">{user.walletAddress}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>


                {/* Action Buttons */}
                <div className="flex gap-3 items-center">
                    {connectedAddress && (
                        <button
                            onClick={onOpenContactModal}
                            className="relative bg-gray-600 hover:bg-gray-500 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 flex items-center gap-2" // Added relative positioning
                            title="View Contacts"
                        >
                            <img src={ContactsIcon} alt="" className="w-5 h-5" />
                            Contacts
                            {/* --- Unread Indicator --- */}
                            {totalUnreadCount > 0 && (
                                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
                                    {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
                                </span>
                            )}
                            {/* --- End Unread Indicator --- */}
                        </button>
                    )}
                    {/* --- Modified Create Stream Button --- */}
                    <button
                        onClick={onOpenStreamConfigModal} // <-- Use the new handler
                        className="bg-[#7db4e5] hover:bg-[#6ca3d1] text-white font-semibold py-2 px-4 rounded-lg transition duration-150"
                        // Optionally disable if not the owner, though Profile.tsx might handle this better
                        // disabled={!isOwnProfile}
                    >
                        Create Stream
                    </button>
                    {/* --- End Modified Create Stream Button --- */}
                </div>
            </div>

            {/* Profile Info Section */}
            <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-8 p-4 bg-[#6d79ad1a] rounded-lg border border-[#ffffff33]">
                {/* Profile Picture */}
                <div className={`relative rounded-full p-0.5 ${getBorderStyle(profilePicture)} transition-all duration-300`}>
                    <img
                        src={getPictureSrc(profilePicture)}
                        alt="Profile"
                        className="w-[76px] h-[76px] sm:w-[92px] sm:h-[92px] rounded-full object-cover"
                    />
                    {isOwnProfile && (
                        <button
                            onClick={onOpenPictureModal}
                            className="absolute -bottom-1 -right-1 bg-gray-700 p-1.5 rounded-full border border-gray-500 hover:bg-gray-600 transition-colors"
                            title="Edit Profile Picture"
                        >
                            <img src={EditIcon} alt="Edit" className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {/* Username, Address, and Additional Info */}
                <div className="flex-grow text-center sm:text-left min-w-0">
                    <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                        <h1 className="text-2xl sm:text-3xl font-bold text-white truncate">
                            {isProfileLoading ? <ClipLoader color={'#ffffff'} size={20} /> : profileUsername || 'User...'}
                        </h1>
                        {isOwnProfile && (
                            <button
                                onClick={onOpenUsernameModal}
                                className="p-1 rounded-full hover:bg-gray-600 transition-colors duration-150 opacity-70 hover:opacity-100 flex-shrink-0"
                                title="Edit Username"
                            >
                                <img src={EditIcon} alt="Edit Username" className="w-5 h-5" />
                            </button>
                        )}
                        {!isOwnProfile && connectedAddress && profileAddress && (
                            <>
                                <button
                                    onClick={onAddContact}
                                    disabled={isAlreadyContact || isContactLoading} // Disable based on contact action loading
                                    className={`p-1.5 rounded-full transition-colors duration-150 flex-shrink-0 ${isAlreadyContact
                                            ? 'text-green-400 cursor-not-allowed'
                                            : 'text-gray-400 hover:text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-wait'
                                        }`}
                                    title={isAlreadyContact ? "Already in Contacts" : "Add to Contacts"}
                                >
                                    {isContactLoading ? ( // Show loader during contact add/remove
                                        <ClipLoader color={'#ffffff'} size={18} />
                                    ) : (
                                        <img src={AddContactIcon} alt={isAlreadyContact ? "Contact Added" : "Add Contact"} className="w-5 h-5" />
                                    )}
                                </button>
                                <button
                                    onClick={onSendMessage}
                                    className="p-1.5 rounded-full transition-colors duration-150 flex-shrink-0 text-gray-400 hover:text-white hover:bg-purple-600 disabled:opacity-50 disabled:cursor-wait"
                                    title="Send Message"
                                >
                                    <img src={MessageIcon} alt="Send Message" className="w-5 h-5" />
                                </button>
                            </>
                        )}
                    </div>
                    
                    <p className="text-sm text-gray-400 break-all px-4 sm:px-0 mb-2">
                        {profileAddress || 'No address available'}
                    </p>
                    
                    {/* Wallet Balance (only for own profile) */}
                    {isOwnProfile && (
                        <div className="mt-2 p-2 bg-gray-800 bg-opacity-50 rounded-md">
                            <div className="flex items-center gap-2 mb-1">
                                <img src={WalletIcon} alt="Wallet" className="w-4 h-4" />
                                <span className="text-gray-300 text-sm font-medium">Internal Wallet</span>
                            </div>
                            
                            {isLoadingWallet ? (
                                <div className="flex items-center justify-center py-1">
                                    <ClipLoader color={'#ffffff'} size={16} />
                                    <span className="ml-2 text-gray-400 text-sm">Loading wallet...</span>
                                </div>
                            ) : renderWalletInfo}
                        </div>
                    )}
                    
                    {/* Private Streams - VERSIÓN MEJORADA */}
                    {(activeStreams?.length > 0 || isLoadingStreams) && (
                        <div className="mt-3 p-2 bg-gray-800 bg-opacity-50 rounded-md">
                          <div className="flex items-center gap-2 mb-1">
                            <img src={TimerIcon} alt="Stream Timer" className="w-4 h-4" />
                            <span className="text-gray-300 text-sm font-medium">
                              {isOwnProfile ? 'Mis Streams' : 'Streams Activos'}
                            </span>
                          </div>
                          
                          {renderActiveStreams()}
                        </div>
                      )}
                    {/* Private Streams Section - Updated version */}
                    {(processedStreams?.length > 0 || isLoadingStreams) && (
                        <div className="mt-3 p-2 bg-gray-800 bg-opacity-50 rounded-md">
                          <div className="flex items-center gap-2 mb-1">
                            <img src={TimerIcon} alt="Stream Timer" className="w-4 h-4" />
                            <span className="text-gray-300 text-sm font-medium">
                              {isOwnProfile ? 'Mis Streams' : 'Streams Activos'}
                            </span>
                          </div>
                          
                          {renderStreams()}
                        </div>
                      )}
                </div>
            </div>
            
            {/* Withdraw Modal */}
            {isWithdrawModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-lg p-5 w-full max-w-sm">
                        <h3 className="text-xl font-bold text-white mb-4">Withdraw Funds</h3>
                        <p className="text-gray-300 mb-3">Available: <span className="text-green-400 font-bold">{parseFloat(walletBalance).toFixed(4)} BNB</span></p>
                        
                        <div className="mb-4">
                            <label className="block text-gray-300 mb-1 text-sm">Amount to Withdraw (BNB)</label>
                            <input
                                type="number"
                                value={withdrawAmount}
                                onChange={(e) => setWithdrawAmount(e.target.value)}
                                step="0.001"
                                min="0.001"
                                max={walletBalance}
                                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                            />
                        </div>
                        
                        <div className="flex justify-end gap-3">
                            <button 
                                onClick={() => setIsWithdrawModalOpen(false)}
                                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={() => {
                                    onWithdraw(withdrawAmount);
                                    setIsWithdrawModalOpen(false);
                                }}
                                disabled={!withdrawAmount || parseFloat(withdrawAmount) <= 0 || parseFloat(withdrawAmount) > parseFloat(walletBalance)}
                                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:text-gray-400 text-white rounded transition-colors"
                            >
                                Withdraw
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* --- End Top Section --- */}
        </>
    );
};

export default ProfileHeader;
